import { useQuery } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import type { TurnstileStatus } from "../shared/types";
import { api, ApiError } from "./api";
import { Brand, BRAND_LOGO_URL } from "./Brand";
import { TurnstileWidget } from "./TurnstileWidget";

type LoginRequest = {
  application: string;
  returnsTo: string;
  scope: string[];
  expiresAt: number;
};

const EXPIRED_MESSAGE =
  "This login request has expired. Return to the application and try again.";

function useSecondsUntil(epochSeconds: number | undefined): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (epochSeconds === undefined) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [epochSeconds]);
  if (epochSeconds === undefined) return undefined;
  return Math.max(0, epochSeconds - Math.floor(now / 1000));
}

function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function LoginPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const transaction = params.get("tx") ?? "";
  const usernameLocked = params.get("username_locked") === "1";
  const [username, setUsername] = useState(params.get("login_hint") ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitExpired, setSubmitExpired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);

  const security = useQuery({
    queryKey: ["login", "security"],
    queryFn: ({ signal }) =>
      api<TurnstileStatus>("/api/login/security", { signal }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const transactionQuery = useQuery({
    queryKey: ["login", "transaction", transaction],
    queryFn: ({ signal }) =>
      api<LoginRequest>(
        `/api/login/transaction?tx=${encodeURIComponent(transaction)}`,
        { signal },
      ),
    enabled: Boolean(transaction),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const turnstile: TurnstileStatus | undefined = security.isError
    ? { enabled: false, state: "misconfigured", missingBindings: [] }
    : security.data;
  const request = transactionQuery.data;
  const remaining = useSecondsUntil(request?.expiresAt);
  const expired =
    submitExpired ||
    remaining === 0 ||
    (transactionQuery.error instanceof ApiError &&
      transactionQuery.error.code === "invalid_login_session");
  const turnstileError = useCallback(() => {
    setTurnstileToken("");
    setError("Human verification could not be completed. Try again.");
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!transaction || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const result = await api<{ redirectTo: string }>("/api/login", {
        method: "POST",
        body: JSON.stringify({
          transaction,
          username,
          password,
          ...(turnstile?.enabled ? { turnstileToken } : {}),
        }),
      });
      window.location.assign(result.redirectTo);
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : undefined;
      if (code === "invalid_login_session") {
        setSubmitExpired(true);
        return;
      }
      setError(
        code === "challenge_failed"
          ? "Human verification expired or failed. Try again."
          : "The username or password is incorrect.",
      );
      setPassword("");
      if (turnstile?.enabled) {
        setTurnstileToken("");
        setTurnstileReset((value) => value + 1);
      }
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <div className="login-card">
        <aside className="login-context" aria-label="Login request">
          <Brand />
          {request ? (
            <>
              <div className="login-chain">
                <div className="login-party">
                  <span className="login-party__icon" aria-hidden="true">
                    {request.application.slice(0, 1).toUpperCase()}
                  </span>
                  <span>
                    <strong>{request.application}</strong>
                    <small>wants to sign you in</small>
                  </span>
                </div>
                <div className="login-chain__link" />
                <div className="login-party">
                  <img
                    alt=""
                    className="login-party__icon login-party__icon--identity"
                    src={BRAND_LOGO_URL}
                  />
                  <span>
                    <strong>CODEMONDAY Identity</strong>
                    <small>verifies who you are</small>
                  </span>
                </div>
              </div>
              <dl className="login-facts">
                <div>
                  <dt>Returns you to</dt>
                  <dd>
                    <code>{request.returnsTo}</code>
                  </dd>
                </div>
                <div>
                  <dt>Shares with {request.application}</dt>
                  <dd>
                    {request.scope.map((scope) => (
                      <code key={scope}>{scope}</code>
                    ))}
                  </dd>
                </div>
              </dl>
              {remaining !== undefined && !expired && (
                <p className="login-expiry">
                  Request expires in {formatCountdown(remaining)}
                </p>
              )}
            </>
          ) : (
            <p className="login-context__blurb">
              One account for every CODEMONDAY internal tool.
            </p>
          )}
        </aside>

        <section className="login-form">
          <h2>Sign in</h2>

          {!transaction ? (
            <p className="login-guidance">
              Open the protected application you want to access to begin signing
              in.
            </p>
          ) : expired ? (
            <p className="login-guidance" role="alert">
              {EXPIRED_MESSAGE}
            </p>
          ) : (
            <form onSubmit={submit} className="form-stack">
              <label>
                <span>Username or email</span>
                <input
                  autoComplete="username"
                  autoFocus={!usernameLocked}
                  maxLength={254}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder=""
                  readOnly={usernameLocked}
                  required
                  value={username}
                />
              </label>
              <label>
                <span>Password</span>
                <div className="password-field">
                  <input
                    autoComplete="current-password"
                    autoFocus={usernameLocked}
                    maxLength={128}
                    minLength={1}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    type={showPassword ? "text" : "password"}
                    value={password}
                  />
                  <button
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    className="text-button"
                    onClick={() => setShowPassword((value) => !value)}
                    type="button"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
              {error && (
                <div className="notice notice--error" role="alert">
                  {error}
                </div>
              )}
              {turnstile?.state === "ready" && turnstile.siteKey && (
                <TurnstileWidget
                  key={turnstileReset}
                  onError={turnstileError}
                  onToken={setTurnstileToken}
                  siteKey={turnstile.siteKey}
                />
              )}
              {turnstile?.state === "misconfigured" && (
                <div className="notice notice--error" role="alert">
                  Sign-in protection is unavailable. Contact an administrator.
                </div>
              )}
              <button
                className="button button--primary button--large"
                disabled={
                  submitting ||
                  !turnstile ||
                  turnstile.state === "misconfigured" ||
                  (turnstile.enabled && !turnstileToken)
                }
                type="submit"
              >
                {submitting ? (
                  <span className="spinner" aria-label="Signing in" />
                ) : request ? (
                  `Continue to ${request.application}`
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
